import { sanitizeTilesetJsonText } from './tileset-json-sanitizer';
import {
  collectExternalTilesetUris,
  isPlyPath,
  namedLodFromUri,
  pickHighestLodPlyUri,
  resolveSplatTileUrls,
  splatMemberUrl,
} from './splats-url-utils';
import { SplatDownloadFields, TilesetJson } from './splats-url-utils.types';
import { SplatLoadTarget } from './resolve-splat-load-target.types';

async function fetchTilesetJson(
  tilesetUrl: string,
  signal?: AbortSignal
): Promise<TilesetJson> {
  const response = await fetch(tilesetUrl, { signal });
  if (!response.ok) {
    throw new Error(
      `Failed to fetch splat tileset (${response.status}): ${tilesetUrl}`
    );
  }

  const sanitized = sanitizeTilesetJsonText(await response.text());
  return JSON.parse(sanitized) as TilesetJson;
}

async function collectPlyUrls(
  tilesetUrl: string,
  templateUrl: string,
  signal: AbortSignal | undefined,
  visited: Set<string>
): Promise<string[]> {
  if (visited.has(tilesetUrl)) {
    return [];
  }
  visited.add(tilesetUrl);

  const tileset = await fetchTilesetJson(tilesetUrl, signal);
  const plyUri = pickHighestLodPlyUri(tileset);
  const fromThisTileset =
    plyUri == null ? [] : [splatMemberUrl(plyUri, tilesetUrl, templateUrl)];

  const fromNestedTilesets = await Promise.all(
    collectExternalTilesetUris(tileset).map((nestedUri) =>
      collectPlyUrls(
        splatMemberUrl(nestedUri, tilesetUrl, templateUrl),
        templateUrl,
        signal,
        visited
      )
    )
  );

  return [...fromThisTileset, ...fromNestedTilesets.flat()];
}

function pickHighestLodPlyUrl(plyUrls: string[]): string | undefined {
  let best: string | undefined;
  for (const current of plyUrls) {
    if (
      best === undefined ||
      namedLodFromUri(current) > namedLodFromUri(best)
    ) {
      best = current;
    }
  }
  return best;
}

/**
 * PLY artifacts load as a single Spark mesh: a loose .ply, or the highest-LOD
 * .ply inside a tileset. Other tilesets keep streamed 3D Tiles loading.
 */
export async function resolveSplatLoadTarget(
  splat: SplatDownloadFields,
  signal?: AbortSignal
): Promise<SplatLoadTarget> {
  if (
    !splat.downloadUrl.includes('*') &&
    (isPlyPath(splat.downloadUrl) || isPlyPath(splat.path))
  ) {
    return { kind: 'ply', url: splat.downloadUrl };
  }

  if (!splat.downloadUrl.includes('*')) {
    throw new Error(
      'Splat downloadUrl must be a .ply file or a fileset URL with a * placeholder'
    );
  }

  const { tilesetUrl, templateUrl } = resolveSplatTileUrls(splat);
  const plyUrl = pickHighestLodPlyUrl(
    await collectPlyUrls(tilesetUrl, templateUrl, signal, new Set<string>())
  );

  if (plyUrl != null) {
    return { kind: 'ply', url: plyUrl };
  }

  return { kind: 'tileset', tilesetUrl, templateUrl };
}
